import { type ExternalToast, Toaster as Sonner, toast as sonnerToast } from 'sonner'

export const UNDO_TOAST_DURATION_MS = 8_000

export type UndoToastOptions = ExternalToast & {
  /** Shows one Undo action for exactly eight seconds. */
  undo?: () => void | Promise<void>
}

function withUndo(options?: UndoToastOptions): ExternalToast | undefined {
  if (!options?.undo) return options
  const { undo, ...rest } = options
  return {
    ...rest,
    duration: UNDO_TOAST_DURATION_MS,
    action: {
      label: 'Undo',
      onClick: () => void undo(),
    },
  }
}

type ToastMessage = Parameters<typeof sonnerToast>[0]
const baseToast = (message: ToastMessage, options?: UndoToastOptions) => sonnerToast(message, withUndo(options))

/** Sonner's API with a first-class `{ undo }` option on ordinary toast variants. */
export const toast = Object.assign(baseToast, sonnerToast, {
  success: (message: ToastMessage, options?: UndoToastOptions) => sonnerToast.success(message, withUndo(options)),
  info: (message: ToastMessage, options?: UndoToastOptions) => sonnerToast.info(message, withUndo(options)),
  warning: (message: ToastMessage, options?: UndoToastOptions) => sonnerToast.warning(message, withUndo(options)),
  error: (message: ToastMessage, options?: UndoToastOptions) => sonnerToast.error(message, withUndo(options)),
  message: (message: ToastMessage, options?: UndoToastOptions) => sonnerToast.message(message, withUndo(options)),
  loading: (message: ToastMessage, options?: UndoToastOptions) => sonnerToast.loading(message, withUndo(options)),
})

/** Toasts are for confirmations of things that already happened in Desk, never for errors that need action. */
export function Toaster() {
  return (
    <Sonner
      position="bottom-right"
      toastOptions={{
        classNames: {
          toast: 'rounded-lg border border-border bg-popover text-popover-foreground shadow-md text-sm',
        },
      }}
    />
  )
}
